import React from "react";

/**
 * Highlights search terms in text with HTML mark tags
 * @param text - The text to search in
 * @param searchTerm - The term to highlight
 * @param className - Optional CSS class for the highlight
 * @returns JSX element with highlighted terms
 */
export function highlightSearchTerm(
  text: string | null | undefined,
  searchTerm: string | null | undefined,
  className = "search-highlight-text",
): React.ReactNode {
  // Return original text if no search term or text
  if (!searchTerm || !text || searchTerm.trim() === "") {
    return text || "";
  }

  const trimmedSearchTerm = searchTerm.trim();

  // Create regex for case-insensitive matching
  const regex = new RegExp(`(${escapeRegExp(trimmedSearchTerm)})`, "gi");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        // Check if this part matches the search term (case-insensitive)
        if (part.toLowerCase() === trimmedSearchTerm.toLowerCase()) {
          return (
            <mark key={index} className={className}>
              {part}
            </mark>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

/**
 * Escapes special regex characters in a string
 * @param string - The string to escape
 * @returns Escaped string safe for regex
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Checks if text contains the search term
 * @param text - Text to search in
 * @param searchTerm - Term to search for
 * @returns true if text contains the search term
 */
export function containsSearchTerm(
  text: string | null | undefined,
  searchTerm: string | null | undefined,
): boolean {
  if (!text || !searchTerm) return false;
  return text.toLowerCase().includes(searchTerm.toLowerCase().trim());
}

/**
 * Scrolls to the first element containing highlighted search terms
 */
export function scrollToFirstHighlight(): void {
  setTimeout(() => {
    const highlightElement = document.querySelector(
      "mark.search-highlight-text",
    );
    if (highlightElement) {
      highlightElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 100);
}

/**
 * Interface for navigation state
 */
export interface HighlightNavigationState {
  currentIndex: number;
  total: number;
}

/**
 * Gets all highlight elements on the page
 * @returns Array of highlight elements
 */
function getAllHighlightElements(): Element[] {
  // Only count actual text highlights, not container elements
  const highlights = Array.from(
    document.querySelectorAll("mark.search-highlight-text"),
  );
  return highlights.filter(
    (el) =>
      el.getBoundingClientRect().width > 0 &&
      el.getBoundingClientRect().height > 0,
  );
}

/**
 * Jumps to the next search highlight on the page
 * @param currentIndex - Current highlight index
 * @returns New navigation state or null if no highlights
 */
export function jumpToNextHighlight(
  currentIndex: number,
): HighlightNavigationState | null {
  const highlights = getAllHighlightElements();

  if (highlights.length === 0) {
    return null;
  }

  // Move to next highlight (with wraparound)
  const newIndex = (currentIndex + 1) % highlights.length;

  const targetHighlight = highlights[newIndex];
  if (targetHighlight) {
    // Remove previous active highlight indicator
    highlights.forEach((el) => el.classList.remove("search-highlight-active"));

    // Add active indicator to current highlight
    targetHighlight.classList.add("search-highlight-active");

    // Scroll to the highlight
    targetHighlight.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    return {
      currentIndex: newIndex,
      total: highlights.length,
    };
  }

  return null;
}

/**
 * Jumps to the previous search highlight on the page
 * @param currentIndex - Current highlight index
 * @returns New navigation state or null if no highlights
 */
export function jumpToPreviousHighlight(
  currentIndex: number,
): HighlightNavigationState | null {
  const highlights = getAllHighlightElements();

  if (highlights.length === 0) {
    return null;
  }

  // Move to previous highlight (with wraparound)
  const newIndex = currentIndex <= 0 ? highlights.length - 1 : currentIndex - 1;

  const targetHighlight = highlights[newIndex];
  if (targetHighlight) {
    // Remove previous active highlight indicator
    highlights.forEach((el) => el.classList.remove("search-highlight-active"));

    // Add active indicator to current highlight
    targetHighlight.classList.add("search-highlight-active");

    // Scroll to the highlight
    targetHighlight.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    return {
      currentIndex: newIndex,
      total: highlights.length,
    };
  }

  return null;
}

/**
 * Gets the count of highlights and current position for display
 * @param currentIndex - Current highlight index
 * @returns Object with current position and total count
 */
export function getHighlightNavigationInfo(currentIndex: number = -1): {
  current: number;
  total: number;
} {
  const highlights = getAllHighlightElements();
  return {
    current: highlights.length > 0 && currentIndex >= 0 ? currentIndex + 1 : 0,
    total: highlights.length,
  };
}

/**
 * Resets the highlight navigation by removing all active highlight indicators
 */
export function resetHighlightNavigation(): void {
  // Remove all active highlight indicators
  document.querySelectorAll(".search-highlight-active").forEach((el) => {
    el.classList.remove("search-highlight-active");
  });
}
